// Header bell + persistent in-app notifications inbox (flow plan stage A,
// backend notifications.py / routers/notifications.py), and the small
// "My profile" popover on the user badge (own email for the Teams / invite /
// email contact actions). The one-shot share toast (notifications.ts) still
// runs; a share now ALSO lands here and stays until read.
import { openAccountSecurity } from "./accountSecurity";
import { directionsControls } from "./directions";
import {
  getUnreadNotificationCount,
  listNotifications,
  listNotificationsSince,
  markAllNotificationsRead,
  markNotificationRead,
  updateOwnProfile,
} from "./api";
import { el } from "./legend";
import { openMyWork } from "./myWork";
import "./styles/flowStageA.css";
import { getState, setState } from "./state";
import { showActionToast } from "./toast";
import type { AppNotification } from "./types";

// flowNav (-> map, deck.gl) is loaded on demand: the field app boots without
// the map half of the app (main.ts / fullApp.ts, 2026-10-05). The event asks
// main.ts to load that half first.
const flowNav = () => {
  document.dispatchEvent(new CustomEvent("wcc-full-app-needed"));
  return import("./flowNav");
};

// Poll faster while the tab is visible (hand-offs show up within ~10 s, the
// showcase's biggest "feels live" gap was the old flat 30 s poll), slowly
// while hidden, and at once when the tab comes back into view.
const POLL_VISIBLE_MS = 10_000;
const POLL_HIDDEN_MS = 60_000;
// More new notifications than this in one poll collapse into one summary
// toast (open issue digests already arrive as one row each, open_issues.py).
const MAX_TOASTS_PER_POLL = 3;
let badge: HTMLSpanElement | null = null;
let dropdown: HTMLDivElement | null = null;
let actionOnly = false;
// Newest notification id seen; null until the first poll of this page load,
// which only records it, so what's already in the inbox doesn't pop up.
let lastSeenId: number | null = null;
let pollTimer: number | null = null;
let polling = false;

function setBadge(n: number): void {
  if (!badge) return;
  badge.textContent = n > 99 ? "99+" : String(n);
  badge.hidden = n === 0;
  badge.parentElement?.setAttribute("aria-label", `Notifications (${n} unread)`);
}

function toastNew(items: AppNotification[]): void {
  // A share already pops its own toast (notifications.ts' share poll).
  const fresh = items.filter((n) => !n.read && n.kind !== "package_shared").sort((a, b) => a.id - b.id);
  if (!fresh.length) return;
  if (fresh.length > MAX_TOASTS_PER_POLL) {
    const nAction = fresh.filter((n) => n.action_needed).length;
    showActionToast({
      title: `${fresh.length} new notifications`,
      body: fresh.slice(-2).map((n) => n.title).join(" · "),
      meta: nAction ? `${nAction} need action · click to open the inbox` : "Click to open the inbox",
      onOpen: openDropdown,
    });
    return;
  }
  for (const n of fresh) {
    showActionToast({
      title: n.title,
      body: n.body,
      meta: `${n.actor_name ? `${n.actor_name} · ` : ""}${n.action_needed ? "action needed · " : ""}click to open`,
      onOpen: () => openTarget(n, null),
      autoDismissMs: n.action_needed ? 30_000 : 15_000,
    });
  }
}

function refreshCount(): Promise<void> {
  return getUnreadNotificationCount()
    .then((r) => {
      setBadge(r.unread);
      const latest = r.latest_id ?? 0;
      if (lastSeenId === null || latest < lastSeenId) {
        lastSeenId = latest; // first poll (or ids went backwards): baseline only
        return;
      }
      if (latest === lastSeenId) return;
      const since = lastSeenId;
      lastSeenId = latest;
      if (dropdown && !dropdown.hidden) renderDropdown();
      listNotificationsSince(since)
        .then(({ items }) => toastNew(items.filter((n) => n.id > since)))
        .catch(() => {});
    })
    .catch(() => {});
}

function schedulePoll(delay?: number): void {
  if (pollTimer !== null) window.clearTimeout(pollTimer);
  const ms = delay ?? (document.visibilityState === "hidden" ? POLL_HIDDEN_MS : POLL_VISIBLE_MS);
  pollTimer = window.setTimeout(() => {
    pollTimer = null;
    if (polling) return;
    polling = true;
    void refreshCount().finally(() => {
      polling = false;
      schedulePoll();
    });
  }, ms);
}

function openDropdown(): void {
  if (!dropdown) return;
  dropdown.hidden = false;
  renderDropdown();
}

/** Jump to a notification's target (brief / project / open issue / digest)
 * and mark it read. Shared by the inbox rows and the live toasts. */
function openTarget(n: AppNotification, li: HTMLElement | null): void {
  if (n.target_type === "brief" && n.target_id) {
    const id = n.target_id;
    void flowNav().then((m) => m.openBrief(id));
    if (dropdown) dropdown.hidden = true;
  } else if (n.target_type === "project" && n.target_id) {
    const id = n.target_id;
    void flowNav().then((m) => m.openProjectById(id)).catch(() => {});
    if (dropdown) dropdown.hidden = true;
  } else if (n.target_type === "msqa_form" && n.target_id) {
    // MSQA site form (msqa.py): "<project id>:<form id>" -> the project's MSQA tab
    const pid = String(n.target_id).split(":")[0];
    void flowNav().then((m) => m.openProjectById(pid, { tab: "msqa" })).catch(() => {});
    if (dropdown) dropdown.hidden = true;
  } else if ((n.target_type === "open_issue" || n.target_type === "my_work") && n.target_id) {
    // My work (myWork.ts): an open issue, or a digest of a workspace's new ones
    const tid = n.target_id;
    void openMyWork(n.target_type === "open_issue" ? { issueId: Number(tid) } : { workspace: tid });
    if (dropdown) dropdown.hidden = true;
  } else if (!li) {
    openDropdown(); // a toast with no target: show it in the inbox
  }
  if (n.read) return;
  markNotificationRead(n.id)
    .then((r) => {
      n.read = true;
      li?.classList.remove("unread");
      setBadge(r.unread);
    })
    .catch(() => {});
}

function when(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString();
}

function itemEl(n: AppNotification): HTMLElement {
  const li = el("li", { className: `notif-item${n.read ? "" : " unread"}` });
  li.append(el("div", { className: "notif-title" }, n.title));
  if (n.body) li.append(el("div", { className: "caption" }, n.body));
  li.append(el("div", { className: "caption" }, `${n.actor_name ? `${n.actor_name} · ` : ""}${when(n.created_at)}${n.action_needed ? " · action needed" : ""}`));
  // a project notification: directions to its site (2026-10-08)
  if (n.target_type === "project" && n.target_id) li.append(directionsControls({ projectId: String(n.target_id) }, { compact: true }));
  li.tabIndex = 0;
  const open = () => openTarget(n, li);
  li.addEventListener("click", open);
  li.addEventListener("keydown", (e) => {
    if (e.key === "Enter") open();
  });
  return li;
}

function renderDropdown(): void {
  if (!dropdown) return;
  dropdown.replaceChildren(el("p", { className: "caption" }, "Loading…"));
  listNotifications()
    .then(({ items, unread }) => {
      if (!dropdown) return;
      setBadge(unread);
      const head = el("div", { className: "notif-head" });
      head.append(el("strong", {}, "Notifications"));
      const filter = el("label", { className: "caption" });
      const cb = el("input", { type: "checkbox", checked: actionOnly }) as HTMLInputElement;
      cb.addEventListener("change", () => {
        actionOnly = cb.checked;
        renderDropdown();
      });
      filter.append(cb, document.createTextNode(" Action needed"));
      const allBtn = el("button", { className: "link-button" }, "Mark all read");
      allBtn.type = "button";
      allBtn.disabled = unread === 0;
      allBtn.addEventListener("click", () => {
        markAllNotificationsRead()
          .then(() => renderDropdown())
          .catch(() => {});
      });
      head.append(filter, allBtn);
      const shown = actionOnly ? items.filter((i) => i.action_needed) : items;
      const list = el("ul", { className: "notif-list" });
      for (const n of shown) list.append(itemEl(n));
      dropdown.replaceChildren(head, shown.length ? list : el("p", { className: "caption" }, "Nothing here yet."));
    })
    .catch(() => dropdown?.replaceChildren(el("p", { className: "caption" }, "Could not load notifications.")));
}

/** Call once after login. Every user (any role / organisation) has an inbox. */
export function initNotificationBell(): void {
  const header = document.getElementById("header");
  const anchor = document.getElementById("tour-help-btn");
  if (!header || document.getElementById("notif-bell")) return;
  const wrap = el("span", { className: "notif-wrap" });
  const btn = el("button", { id: "notif-bell", title: "Notifications" }, "🔔");
  btn.type = "button";
  badge = el("span", { className: "notif-badge" });
  badge.hidden = true;
  btn.append(badge);
  dropdown = el("div", { className: "notif-dropdown" }) as HTMLDivElement;
  dropdown.hidden = true;
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    dropdown!.hidden = !dropdown!.hidden;
    if (!dropdown!.hidden) renderDropdown();
  });
  dropdown.addEventListener("click", (e) => e.stopPropagation());
  document.addEventListener("click", () => {
    if (dropdown) dropdown.hidden = true;
  });
  wrap.append(btn, dropdown);
  header.insertBefore(wrap, anchor ?? null);
  void refreshCount();
  schedulePoll();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") schedulePoll(0);
  });
  initProfilePopover();
}

/** Click the user badge -> set your own email (used by contact actions). */
function initProfilePopover(): void {
  const info = document.getElementById("current-user-info");
  if (!info || info.dataset.profileWired) return;
  info.dataset.profileWired = "1";
  info.style.cursor = "pointer";
  info.title = "My profile (email)";
  const pop = el("div", { className: "notif-dropdown profile-popover" }) as HTMLDivElement;
  pop.hidden = true;
  info.after(pop);
  pop.addEventListener("click", (e) => e.stopPropagation());
  document.addEventListener("click", () => {
    pop.hidden = true;
  });
  info.addEventListener("click", (e) => {
    e.stopPropagation();
    pop.hidden = !pop.hidden;
    if (pop.hidden) return;
    const user = getState().currentUser;
    const form = el("form");
    const input = el("input", { type: "email", placeholder: "name@organisation", value: user?.email ?? "" }) as HTMLInputElement;
    const save = el("button", { type: "submit" }, "Save");
    const status = el("span", { className: "caption" });
    form.append(el("label", {}, "Email "), input, save, status);
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      save.disabled = true;
      updateOwnProfile({ email: input.value.trim() })
        .then((u) => {
          setState({ currentUser: { ...getState().currentUser!, email: u.email ?? null } });
          status.textContent = "Saved";
        })
        .catch(() => {
          status.textContent = "Not a valid email address";
        })
        .finally(() => {
          save.disabled = false;
        });
    });
    // Two-factor sign-in (accountSecurity.ts): turn on / new phone / off
    const mfaBtn = el("button", { type: "button" }, user?.mfa_enabled ? "Manage" : "Turn on");
    mfaBtn.addEventListener("click", () => {
      pop.hidden = true;
      openAccountSecurity();
    });
    const mfaRow = el("p", { className: "caption" });
    mfaRow.append(`Two-factor sign-in: ${user?.mfa_enabled ? "on" : "off"} `, mfaBtn);
    pop.replaceChildren(
      el("strong", {}, "My profile"),
      el("p", { className: "caption" }, "Your email is used for Teams chats, meeting invites and email drafts other people open from WRAP."),
      form,
      mfaRow,
    );
  });
}
